/**
 * Chứng cứ trong Thư ký tòa án — gộp từ AI Chứng cứ, bỏ phân loại/bộ lọc trái & panel AI phải.
 * Bố cục dọc giống các khối Bảng điều khiển.
 */
import { useState } from 'react'
import { useAppSelector } from '../../hooks/redux'
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
} from '@mui/material'
import { Warning, CompareArrows, CheckCircle, Star, StarBorder } from '@mui/icons-material'
import WorkspaceSectionHeading from './WorkspaceSectionHeading'
import { brandColors } from '../../theme'

const SOURCE_COLORS = { court: brandColors.primary, plaintiff: '#ea580c', defendant: '#7c3aed' }

interface EvidenceCard {
  id: string
  source: 'court' | 'plaintiff' | 'defendant'
  date: string
  code: string
  title: string
  excerpt: string
  conflict: boolean
  conflictWith?: string
}

const EVIDENCE_CARDS: EvidenceCard[] = [
  { id: 'TA-001', source: 'court', date: '01/2023', code: 'TA-001', title: 'Biên bản xác minh thực địa', excerpt: 'Kết quả xác minh tại thửa đất 123, tờ bản đồ 45. Hiện trạng sử dụng đất.', conflict: false },
  { id: 'TA-002', source: 'court', date: '02/2023', code: 'TA-002', title: 'Kết quả giám định đất', excerpt: 'Giám định viên kết luận diện tích, ranh giới theo hồ sơ địa chính.', conflict: false },
  { id: 'ND-001', source: 'plaintiff', date: '08/2023', code: 'ND-001', title: 'Giấy chứng nhận QSDD', excerpt: 'GCN QSDD số 123/2023, cấp cho hộ ông Nguyễn Văn A.', conflict: false },
  { id: 'ND-002', source: 'plaintiff', date: '10/2023', code: 'ND-002', title: 'Hợp đồng mua bán', excerpt: 'HĐMB ngày 15/10/2020. Có thể mâu thuẫn với kết quả giám định TA-002 về thời điểm.', conflict: true, conflictWith: 'TA-002' },
  { id: 'BD-001', source: 'defendant', date: '09/2023', code: 'BD-001', title: 'Văn bản khiếu nại', excerpt: 'Đơn khiếu nại lên UBND. Chưa có bản sao công chứng.', conflict: false },
  { id: 'BD-002', source: 'defendant', date: '04/2025', code: 'BD-002', title: 'Biên lai nộp thuế', excerpt: 'Biên lai thuế sử dụng đất các năm 2020–2024.', conflict: false },
]

export default function CourtSecretaryEvidencePanel() {
  const user = useAppSelector((s) => s.auth.user)
  const [evidenceApproved, setEvidenceApproved] = useState(false)
  const [importantIds, setImportantIds] = useState<Set<string>>(new Set())
  const [viewDocId, setViewDocId] = useState<string | null>(null)
  const [comparePair, setComparePair] = useState<{ a: string; b: string } | null>(null)
  const [snack, setSnack] = useState('')
  const isChief = user?.role === 'chief_judge' || user?.role === 'admin'

  const toggleImportant = (id: string) => {
    setImportantIds((prev) => {
      const next = new Set(prev)
      const wasImportant = next.has(id)
      if (wasImportant) next.delete(id)
      else next.add(id)
      setSnack(wasImportant ? 'Đã bỏ đánh dấu quan trọng.' : 'Đã đánh dấu quan trọng.')
      setTimeout(() => setSnack(''), 2000)
      return next
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 2 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 0 }}>
        <WorkspaceSectionHeading title="Trục thời gian" subtitle="Các mốc chứng cứ theo thời gian (demo vụ Tranh chấp QSDĐ – Long An)." />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'auto', pb: 1, flexWrap: 'nowrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <Typography variant="caption">Tháng 1/2023</Typography>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS.plaintiff, mt: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, height: 2, bgcolor: 'divider', minWidth: 40 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <Typography variant="caption">Tháng 6/2023</Typography>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS.court, mt: 0.5 }} />
          </Box>
          <Box sx={{ flex: 1, height: 2, bgcolor: 'divider', minWidth: 40 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <Typography variant="caption">Tháng 4/2025</Typography>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS.defendant, mt: 0.5 }} />
          </Box>
        </Box>
      </Paper>

      <Box>
        <WorkspaceSectionHeading title="Danh sách chứng cứ" subtitle="Vụ án: Tranh chấp QSDĐ tại Long An (2023/TLS-CDS)" />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {EVIDENCE_CARDS.map((card) => (
            <Card key={card.id} variant="outlined" sx={{ borderRadius: 0, borderColor: importantIds.has(card.id) ? 'primary.main' : 'divider' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SOURCE_COLORS[card.source] }} />
                  <Typography variant="body2" fontWeight={600}>
                    {card.date} – {card.code} {card.title}
                  </Typography>
                  {importantIds.has(card.id) && <Star color="primary" fontSize="small" />}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {card.excerpt}
                </Typography>
                {card.conflict && (
                  <Chip icon={<Warning />} label={`Mâu thuẫn với ${card.conflictWith}`} size="small" color="error" sx={{ mt: 1 }} />
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setViewDocId(card.id)}>
                    Xem
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setSnack('Đã lưu chú thích (demo).')
                      setTimeout(() => setSnack(''), 2000)
                    }}
                  >
                    Chú thích
                  </Button>
                  <Button size="small" startIcon={importantIds.has(card.id) ? <Star /> : <StarBorder />} onClick={() => toggleImportant(card.id)}>
                    {importantIds.has(card.id) ? 'Bỏ đánh dấu' : 'Đánh dấu quan trọng'}
                  </Button>
                  {card.conflict && card.conflictWith && (
                    <Button size="small" startIcon={<CompareArrows />} onClick={() => setComparePair({ a: card.id, b: card.conflictWith! })}>
                      So sánh ngay
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {isChief && (
        <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 0 }}>
          <Typography variant="subtitle2" gutterBottom>
            Chánh án phê duyệt
          </Typography>
          {evidenceApproved ? (
            <Alert severity="success" icon={<CheckCircle />}>
              Đã phê duyệt phân tích chứng cứ đưa vào hồ sơ chính thức.
            </Alert>
          ) : (
            <Button variant="contained" color="primary" startIcon={<CheckCircle />} onClick={() => setEvidenceApproved(true)}>
              Phê duyệt phân tích chứng cứ đưa vào hồ sơ
            </Button>
          )}
        </Paper>
      )}

      <Dialog open={!!viewDocId} onClose={() => setViewDocId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>Xem chứng cứ – {viewDocId}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            {EVIDENCE_CARDS.find((c) => c.id === viewDocId)?.excerpt ?? ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Nội dung đầy đủ (file PDF/tài liệu) sẽ hiển thị tại đây khi tích hợp thật.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={!!comparePair} onClose={() => setComparePair(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>So sánh chứng cứ</DialogTitle>
        <DialogContent>
          <Typography variant="body2" fontWeight={600}>
            {comparePair?.a} vs {comparePair?.b}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Phát hiện mâu thuẫn về thời điểm/ngày ký giữa hai tài liệu. Nên xem xét làm rõ với các bên.
          </Typography>
        </DialogContent>
      </Dialog>

      <Snackbar open={!!snack} message={snack} autoHideDuration={2000} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  )
}
